import { useQuery } from '@tanstack/react-query'
import {
	ActionBar,
	AddIcon,
	Checkbox,
	Dialog,
	Label,
	Link,
	ModuleHeader,
	SettingsIcon,
	SpinnerWithBackground,
	Text,
	UsersIcon,
} from '@viapeople/components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { EvaluationHeaderModal } from '../../components/EvaluationHeaderModal'
import { type Column, GenericTable } from '../../components/GenericTable'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import UserFilesModal from './components/UserFilesModal'
import { UserUploadModal } from './components/UserUploadModal'
import type { SearchQuery, User, UserDashboardStoreInstance } from './stores/userDashboardStore'

interface Props {
	store: UserDashboardStoreInstance
}

export function UsersPage({ store }: Props) {
	const {
		users,
		actions,
		selectedUsers,
		organization,
		availableFields,
		isLoading,
		isExporting,
		error,
		initialize,
		searchUsers,
		setSelectedUsers,
		archiveSelectedUsers,
		activateSelectedUsers,
		deactivateSelectedUsers,
		exportUsers,
		setSearchQuery,
		setError,
	} = store()

	const { organizationId } = useParams<{ organizationId: string }>()
	const navigate = useNavigate()
	const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
	const [isFilesModalOpen, setIsFilesModalOpen] = useState(false)
	const [isHeaderConfigModalOpen, setIsHeaderConfigModalOpen] = useState(false)
	const toast = useToast()
	const [isEmpty, setIsEmpty] = useState(false)
	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
	const [includeInactive, setIncludeInactive] = useState(true)
	const searchTimeoutRef = useRef<NodeJS.Timeout>(null)
	const { data: populationLock } = useQuery({
		...trpc.admin.users.getPopulationLockStatus.queryOptions({ organizationId: organizationId as string }),
		enabled: !!organizationId,
	})
	const isPopulationLockKnown = populationLock !== undefined
	const isPopulationLocked = populationLock?.locked === true
	const canMutatePopulation = isPopulationLockKnown && !isPopulationLocked
	// Match server-declared capabilities (userDashboard.init) with runtime lock; same pattern as New Upload.
	const canUploadUsers = actions.includes('uploadUsers') && canMutatePopulation
	const canCreateUser = actions.includes('createUser') && canMutatePopulation

	useEffect(() => {
		if (organizationId) {
			initialize(organizationId)
		}
	}, [initialize, organizationId])

	useEffect(() => {
		if (error) {
			toast.showToast(error, 'error')
			setError()
		}
	}, [error, toast, setError])

	const handleOpenUploadModal = () => {
		setIsUploadModalOpen(true)
	}

	const handleCloseUploadModal = () => {
		setIsUploadModalOpen(false)
	}

	const handleOpenFilesModal = () => {
		setIsFilesModalOpen(true)
	}

	const handleCloseFilesModal = () => {
		setIsFilesModalOpen(false)
	}

	const handleOpenHeaderConfigModal = () => {
		setIsHeaderConfigModalOpen(true)
	}

	const handleCloseHeaderConfigModal = () => {
		setIsHeaderConfigModalOpen(false)
	}

	const handleUploadSuccess = () => {
		toast.showToast('Users updated')
		if (organizationId) {
			initialize(organizationId)
		}
	}

	const handleSearchChange = useCallback(
		(query: Omit<SearchQuery, 'organizationId'>) => {
			setSearchQuery(query)

			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}

			searchTimeoutRef.current = setTimeout(() => {
				if (organizationId) {
					searchUsers()
				}
			}, 300)
		},
		[organizationId, setSearchQuery, searchUsers],
	)

	const columns: Column<User>[] = [
		{
			field: 'firstName',
			label: 'Name',
			colSpan: 2,
			minWidth: '180px',
			render: (user: User) => `${user.firstName} ${user.lastName}`,
		},
		{
			field: 'externalId',
			label: 'ID',
			colSpan: 1,
			minWidth: '100px',
		},
		{
			field: 'email',
			label: 'Email',
			colSpan: 2,
			minWidth: '200px',
		},
		...((availableFields?.customFields || []).map(({ key, type }) => ({
			field: key as keyof User,
			label: key,
			sortable: true,
			sortPrefix: 'customFields.',
			colSpan: 2,
			minWidth: '150px',
			render: (user: User) => {
				const customFieldsData = user.customFields as Record<string, unknown> | undefined
				const raw = customFieldsData?.[key]
				if (raw === null || raw === undefined || raw === '') return ''
				if (type === 'number') {
					const n = Number(raw)
					return Number.isNaN(n) ? String(raw) : n.toLocaleString()
				}
				return String(raw)
			},
		})) || []),
		{
			field: 'active',
			label: 'Status',
			minWidth: '100px',
			render: (user: User) => (
				<div className='flex'>
					<Label className='self-start' color={user.active ? 'green' : 'default'}>
						{user.active ? 'Active' : 'Inactive'}
					</Label>
				</div>
			),
		},
	]

	if (isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	if (!organization) {
		return (
			<main className='p-6'>
				<Text>Error loading users. Please try again.</Text>
				<Link href='/organizations' variant='default'>
					Back to Organizations
				</Link>
			</main>
		)
	}

	return (
		<main className='w-full p-6 flex flex-col gap-4'>
			{isPopulationLocked && (
				<div className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-3'>
					<Text variant='small' className='text-amber-900'>
						{populationLock?.reason ??
							'Population editing is locked because this organization has a Data Feed configured.'}
					</Text>
				</div>
			)}
			<ModuleHeader
				breadcrumb={[
					{ label: 'Organizations', to: '/organizations' },
					{ label: organization.name, to: `/organizations/${organizationId}` },
					{ label: 'Users' },
				]}
				title='Users'
				logo={{
					url: organization.logo,
					name: organization.name,
				}}
				buttons={[
					...(canUploadUsers
						? [
								{
									label: 'New Upload',
									variant: 'primary' as const,
									onClick: handleOpenUploadModal,
								},
							]
						: []),
					...(!isEmpty && actions.includes('viewUserFiles')
						? [
								{
									label: 'Data Files',
									variant: 'secondary' as const,
									onClick: handleOpenFilesModal,
								},
							]
						: []),
					...(!isEmpty && actions.includes('configureHeader')
						? [
								{
									label: 'Configure Evaluation Header',
									variant: 'secondary' as const,
									onClick: handleOpenHeaderConfigModal,
									icon: SettingsIcon,
								},
							]
						: []),
					...(isEmpty
						? []
						: [
								{
									label: isExporting ? 'Exporting...' : 'Export',
									variant: 'secondary' as const,
									onClick: () => {
										if (!isExporting) {
											exportUsers(includeInactive).then((success) => {
												if (success) {
													toast.showToast('Users exported successfully')
												} else {
													toast.showToast('Failed to export users. Please try again.', 'error')
												}
											})
										}
									},
								},
							]),
				]}
			/>
			{!isEmpty && (
				<div className='flex justify-end'>
					<Checkbox
						checked={includeInactive}
						onChange={(e) => setIncludeInactive(e.target.checked)}
						label='Include inactive users in export'
						size='small'
						data-testid='include-inactive-checkbox'
					/>
				</div>
			)}

			<GenericTable<User, SearchQuery>
				data={users}
				columns={columns}
				idField='id'
				selectable
				onSearchChange={handleSearchChange}
				onStatusChange={(isEmpty) => setIsEmpty(isEmpty)}
				onSelectionChange={setSelectedUsers}
				onRowClick={canMutatePopulation ? (user) => navigate(`${user.id}`) : undefined}
				actionButtons={
					canCreateUser
						? [
								{
									icon: AddIcon,
									iconPosition: 'left',
									children: 'Add User',
									variant: 'tertiary',
									onClick: () => navigate('create'),
								},
							]
						: []
				}
				emptyState={{
					icon: UsersIcon,
					title: 'No user data to display',
					description: 'Get started by uploading a file',
				}}
			/>

			<UserUploadModal
				isOpen={isUploadModalOpen}
				onClose={handleCloseUploadModal}
				onUploadSuccess={handleUploadSuccess}
			/>

			<UserFilesModal
				isOpen={isFilesModalOpen}
				onClose={handleCloseFilesModal}
				organizationId={organizationId as string}
			/>

			<EvaluationHeaderModal
				isOpen={isHeaderConfigModalOpen}
				onClose={handleCloseHeaderConfigModal}
				organizationId={organizationId as string}
			/>

			<Dialog
				isOpen={isArchiveModalOpen}
				onClose={() => setIsArchiveModalOpen(false)}
				onConfirm={archiveSelectedUsers}
				title='Archive users'
				message={`You are about to archive ${selectedUsers.length} ${selectedUsers.length === 1 ? 'user' : 'users'} from this organization. This action cannot be reversed.`}
				type='archive'
				confirmLabel='Archive'
				requireConfirmText
			/>
			{canMutatePopulation && selectedUsers.length > 0 && (
				<ActionBar
					rightActions={[
						...(selectedUsers.some((user) => user.active)
							? [
									{
										label: 'Set as inactive',
										variant: 'secondary' as const,
										onClick: () => {
											deactivateSelectedUsers()
											toast.showToast('Users set to inactive')
										},
									},
								]
							: []),
						...(selectedUsers.some((user) => !user.active)
							? [
									{
										label: 'Set as active ',
										variant: 'secondary' as const,
										onClick: () => {
											activateSelectedUsers()
											toast.showToast('Users set to active')
										},
									},
								]
							: []),
						{
							label: `Archive ${selectedUsers.length > 1 ? `(${selectedUsers.length})` : ''}`,
							variant: 'destructive',
							onClick: () => setIsArchiveModalOpen(true),
						},
					]}
				/>
			)}
		</main>
	)
}
